/** @jsx jsx */
import { jsx } from '@emotion/react';

import type { ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { Grid } from '@coursera/cds-core';

import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import type { SearchHit } from 'bundles/search-common/providers/searchTypes';
import CoachChatContainer from 'bundles/search-v2/components/experiments/coachInSearch/CoachChatContainer';
import { usePageHeaderFooterContext } from 'bundles/search-v2/components/experiments/coachInSearch/PageHeaderFooterContext';
import { coachAdditionalContext, coachConfig } from 'bundles/search-v2/components/experiments/coachInSearch/constants';
import { filterForValidChatProducts } from 'bundles/search-v2/components/experiments/coachInSearch/utils';
import { impressCoachInSearch, showCoachInSearch } from 'bundles/search-v2/utils/experiments';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';

type Props = {
  children: ReactNode;
  searchHits: SearchHit[];
  isEligibleForCoachInSearch: boolean;
};

// we need to limit the additionalProducts so we don't explode coach
const MaxChatProducts = 5;

export const CoachChatLayer = (props: Props) => {
  const { searchHits, children, isEligibleForCoachInSearch } = props;

  const { headerRef, footerRef } = usePageHeaderFooterContext();

  const { coachOpen, setCoachOpen } = useCoachState();

  const chatProducts = useMemo(() => filterForValidChatProducts(searchHits).slice(0, MaxChatProducts), [searchHits]);

  const showCoach = isEligibleForCoachInSearch && showCoachInSearch() && coachOpen;

  useEffect(() => {
    if (isEligibleForCoachInSearch) {
      impressCoachInSearch();
    }
  }, [isEligibleForCoachInSearch]);

  return (
    <Grid container>
      <Grid item xs={showCoach ? 9 : 12}>
        {children}
      </Grid>
      {showCoach && (
        <Grid item xs={3}>
          <CoachChatContainer headerRef={headerRef} footerRef={footerRef}>
            <CoachChat
              onClose={() => setCoachOpen(false)}
              showSettings
              mode="SEARCH_DISCOVERY_MVP_MODE_V1"
              isFullScreen={false}
              contextConfig={{
                courseraContext: {
                  additionalProducts: chatProducts,
                },
                config: { ...coachConfig },
                additionalContext: [{ ...coachAdditionalContext }],
              }}
            />
          </CoachChatContainer>
        </Grid>
      )}
    </Grid>
  );
};
